import { useState } from "react";
import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { disciplineService } from "@/frontend/services";
import type { PaginationRequestDTO, DisciplineResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";
import { DatePicker } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: PaginationRequestDTO = { page: 1, pageSize: 7 };

export function DisciplinesListPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const { data, loading, pagination, loadData } = usePagination<DisciplineResponseDTO>({
    fetchFn: disciplineService.getPaginate,
    initialParams: initialParams,
    initialPagination: initialPagination,
  });

  function applyFilters(range?: [string, string] | null) {
    loadData({ page: 1, pageSize: 7, createdFrom: range?.[0], createdTo: range?.[1] });
  }

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } = useDelete({
    deleteFn: disciplineService.delete,
    loadData,
    successMessage: "Disciplina eliminada exitosamente",
    errorMessage: "Error al eliminar disciplina",
    showConfirm: true,
    confirmTitle: "¿Eliminar disciplina?",
    confirmDescription: "Esta acción eliminará la disciplina permanentemente. ¿Estás seguro?",
  });

  const columns: ColumnsType<DisciplineResponseDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (desc: string | null) => desc || "-",
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      render: (color: string | null) => {
        if (!color) return "-";
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded border border-gray-300"
              style={{ backgroundColor: color }}
            />
            <span>{color}</span>
          </div>
        );
      },
    },
    {
      title: "Orden",
      dataIndex: "sortOrder",
      key: "sortOrder",
    },
    {
      title: "Sistema",
      dataIndex: "isSystem",
      key: "isSystem",
      render: (val: boolean) => (val ? "Sí" : "No"),
    },
    {
      title: "Editable",
      dataIndex: "canEdit",
      key: "canEdit",
      render: (val: boolean) => (val ? "Sí" : "No"),
    },
    {
      title: "Eliminable",
      dataIndex: "canDelete",
      key: "canDelete",
      render: (val: boolean) => (val ? "Sí" : "No"),
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: unknown, record: DisciplineResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaEdit />}
            onClick={() => navigate(`/disciplines/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                danger
                icon={<FaTrash />}
              >
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Disciplinas</h2>
          <p className="text-gray-600 mt-1">
            Administra las disciplinas deportivas disponibles en la plataforma
          </p>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
